import React, { useMemo } from 'react';
import { PromptView } from './promptView';

export const PromptListView = ({
    processes = [],
    currentProcess,
    selectedProcess,
    selectedPrompt,
    setSelectedPrompt,
    isPersonaProcess,
    isAdmin,
    expandPromptId,

    // helpers
    getDuplicatePersonaKeys,
    getPersonaPromptNames,
    normalizePromptKey,
    normalizePromptName,
    promptDomId,
    formatProcessName,

    // handlers
    handleInstructionChange,
    handlePromptNameChange,
    handleCopyPersonaPrompt,
    handleDeletePersonaPrompt,
}) => {
    const normalizePromptNameSafe =
        normalizePromptName || ((name) => (name || '').trim());
    const normalizePromptKeySafe =
        normalizePromptKey || ((name) => normalizePromptNameSafe(name).toLowerCase());
    const formatProcessNameSafe =
        formatProcessName || ((name) => String(name || ''));
    const promptDomIdSafe =
        promptDomId ||
        ((processName, promptName) =>
            `prompt-${String(processName || 'unknown')}-${normalizePromptKeySafe(promptName) || 'unnamed'}`);

    const duplicatePromptKeys = useMemo(() => {
        if (!isPersonaProcess) return new Set();
        if (!getDuplicatePersonaKeys || !getPersonaPromptNames || !normalizePromptKeySafe) {
            return new Set();
        }
        return getDuplicatePersonaKeys(
            processes,
            getPersonaPromptNames,
            normalizePromptKeySafe
        );
    }, [
        isPersonaProcess,
        processes,
        getDuplicatePersonaKeys,
        getPersonaPromptNames,
        normalizePromptKeySafe,
    ]);

    if (!processes.length || !currentProcess?.sub_tasks?.length) {
        return null;
    }

    return (
        <div className='px-2'>
            {currentProcess.sub_tasks.map((task, idx) => {
                const promptKey = normalizePromptKeySafe(task?.prompt);
                const displayPromptName = task?._displayName || task?.prompt;

                const isUnsaved = task?._isUnsaved === true;
                const isCustom =
                    task?.is_custom === true ||
                    task?.is_custom === 1 ||
                    task?.is_custom === 'true' ||
                    task?.is_custom === 'True';

                const isSystemPersonaPrompt =
                    isPersonaProcess && !isUnsaved && !isCustom;

                const isTaskReadOnly =
                    !!isPersonaProcess && task?._readOnly === true;

                const isSharedReadOnlyPersona =
                    !!isPersonaProcess && isTaskReadOnly && task?._source === 'shared';

                const canEditPersonaInstruction =
                    !isPersonaProcess ||
                    (!isTaskReadOnly && (isAdmin || isUnsaved || isCustom));

                let instructionDisabledReason = '';
                if (isTaskReadOnly) {
                    if (task?._source === 'shared' && task?.ownerUsername) {
                        instructionDisabledReason = `Only ${task.ownerUsername} can edit this prompt. Copy to make your own version.`;
                    } else if (task?._source === 'system' && !isAdmin) {
                        instructionDisabledReason = 'Only admins can edit system persona prompts.';
                    } else {
                        instructionDisabledReason = 'This prompt is read-only. Copy to modify it.';
                    }
                } else if (isSystemPersonaPrompt && !isAdmin) {
                    instructionDisabledReason = 'Only admins can edit system persona prompts.';
                }

                const isUserAddedPersonaPrompt =
                    !!isPersonaProcess && (isUnsaved || isCustom) && !isTaskReadOnly;

                const promptNameError =
                    isPersonaProcess &&
                    task?._isUnsaved &&
                    promptKey &&
                    duplicatePromptKeys.has(promptKey)
                        ? 'Prompt name cannot be the same. Please change the name of the prompt.'
                        : '';

                const domId =
                    task?._uiId ||
                    promptDomIdSafe(selectedProcess, task?.prompt);

                const hasWarningProps =
                    Object.prototype.hasOwnProperty.call(task || {}, 'active') &&
                    Object.prototype.hasOwnProperty.call(
                        task || {},
                        'warning_message'
                    );

                const showWarning =
                    !!hasWarningProps && task?.active === false && !!task?.warning_message;

                return (
                    <div key={domId} id={domId} className='scroll-mt-24'>
                        <PromptView
                            index={idx}
                            prompt={task.prompt}
                            displayPromptName={displayPromptName}
                            instruction={task.instruction}
                            onInstructionChange={(val) =>
                                handleInstructionChange(idx, val)
                            }
                            onPromptNameChange={(val) =>
                                handlePromptNameChange(idx, val)
                            }
                            formatProcessName={formatProcessNameSafe}
                            selectedProcess={selectedProcess}
                            promptNameEditable={!!isPersonaProcess && !!task?._isUnsaved}
                            promptNameError={promptNameError}
                            instructionEditable={canEditPersonaInstruction}
                            instructionDisabledReason={instructionDisabledReason}
                            allowCopy={isPersonaProcess}
                            onCopy={() => handleCopyPersonaPrompt?.(idx)}
                            allowReset={
                                !isPersonaProcess ||
                                (!!isAdmin && !!isPersonaProcess &&
                                    !isUserAddedPersonaPrompt &&
                                    !isSharedReadOnlyPersona)
                            }
                            showResetButton={
                                !isPersonaProcess ||
                                (!!isAdmin && !!isPersonaProcess &&
                                    !isUserAddedPersonaPrompt &&
                                    !isSharedReadOnlyPersona)
                            }
                            showDeleteButton={
                                !!isPersonaProcess && isUserAddedPersonaPrompt
                            }
                            onDelete={() =>
                                handleDeletePersonaPrompt?.(idx, task)
                            }
                            autoExpand={expandPromptId === domId}
                            selectedPrompt={selectedPrompt}
                            setSelectedPrompt={setSelectedPrompt}
                            showWarning={showWarning}
                            warningMessage={
                                hasWarningProps ? task?.warning_message : ''
                            }
                        />
                    </div>
                );
            })}
        </div>
    );
};
